
/**
 * Quem vai a cada etapa: um responsável e, se houver, um ajudante.
 *
 * Mora no SERVIÇO (`services.equipe_das_etapas`), uma entrada por etapa da
 * corrente, e não na sessão de cada etapa. A sessão só nasce quando a etapa é
 * lançada, e a equipe precisa existir antes — é ela que o calendário mostra
 * para o veterinário saber com quem vai, dias antes de a OPU acontecer.
 *
 *   { opu: { responsavel_id, responsavel_nome, ajudante_id, ajudante_nome }, … }
 *
 * Id E nome, pela mesma razão das sessões: técnico sai da equipe, é
 * desativado ou excluído, e o serviço de dois anos atrás ainda tem de dizer
 * quem foi.
 *
 * O ajudante também é um técnico — veterinário ajuda veterinário —, então os
 * dois saem da mesma lista.
 */

export const PAPEIS = ['responsavel', 'ajudante'];

// Eventos do calendário que não são etapa com equipe própria: a retirada é o
// mesmo protocolo, quem implantou é quem retira.
const ETAPA_DO_EVENTO = { retirada: 'protocolo' };

/** A equipe de uma etapa (ou de um evento derivado dela). Nunca `undefined`. */
export const equipeDaEtapa = (equipes, etapaId) =>
  (equipes || {})[ETAPA_DO_EVENTO[etapaId] || etapaId] || {};

/** `{ id, nome }` de um papel, ou `null` quando ninguém foi escolhido. */
export const pessoa = (equipe, papel) => {
  const id = equipe?.[`${papel}_id`] || null;
  const nome = equipe?.[`${papel}_nome`] || '';
  return id || nome ? { id, nome } : null;
};

/**
 * Põe alguém num papel de UMA etapa e devolve as equipes novas.
 *
 * Só aquela etapa. A escolha já escorreu para as seguintes vazias (é quase
 * sempre a mesma dupla), mas em silêncio: quem escalava a TE achava o DG
 * preenchido sem ter decidido, e etapa vazia é resposta válida — "ainda não
 * se sabe quem vai". O que não foi escolhido fica vazio.
 */
export const definirNaEtapa = (equipes, etapaId, papel, id, nome) => {
  const atual = { ...(equipes || {}) };
  atual[etapaId] = { ...(atual[etapaId] || {}), [`${papel}_id`]: id || null, [`${papel}_nome`]: id ? (nome || '') : '' };
  return atual;
};

/** "Fulano + Breno", "Fulano", ou `''`. Para o calendário e para a busca. */
export const nomesDaEquipe = (equipe) =>
  [pessoa(equipe, 'responsavel'), pessoa(equipe, 'ajudante')]
    .filter(Boolean).map(p => p.nome).filter(Boolean).join(' + ');

/** Todos os nomes de todas as etapas — o que a busca procura. */
export const todosOsNomes = (equipes) =>
  Object.values(equipes || {}).flatMap(e => PAPEIS.map(p => e?.[`${p}_nome`])).filter(Boolean);

/**
 * A chave de uma pessoa: o id, ou o nome quando a equipe foi gravada sem id.
 * Prefixada para um nome nunca colidir com um id.
 */
const chaveDe = (p) => (p?.id ? `id:${p.id}` : p?.nome ? `nome:${p.nome}` : null);

/**
 * Quem aparece nas equipes destes serviços, em ordem alfabética.
 *
 * Sai dos serviços, e não do cadastro de técnicos: quem foi desativado ainda
 * tem etapa marcada, e quem nunca foi escalado não tem o que filtrar. Responsável
 * e ajudante na mesma lista — a pergunta é "onde o Breno vai estar?", não em
 * que papel.
 */
export const pessoasDasEquipes = (services = []) => {
  const porChave = new Map();
  (services || []).forEach(s => Object.values(s?.equipe_das_etapas || {}).forEach(equipe =>
    PAPEIS.forEach(papel => {
      const p = pessoa(equipe, papel);
      const chave = chaveDe(p);
      if (chave && p.nome && !porChave.has(chave)) porChave.set(chave, { chave, nome: p.nome });
    })));
  return [...porChave.values()].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
};

/** A pessoa está nesta equipe, em qualquer papel? */
export const estaNaEquipe = (equipe, chave) =>
  !!chave && PAPEIS.some(papel => chaveDe(pessoa(equipe, papel)) === chave);
